import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { createContext, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetLoggedInUser } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { FilterId } from '../../filters/types/filters';
import { usePageMutations } from '../../hooks/usePageMutations';
import { pageQueryKeys, usePagesQuery } from '../../hooks/usePagesQuery';
import {
  cloneAndSanitizePage,
  getExistingPageNames,
  getOriginalPage,
} from '../../utils/generate-page-draft';
import { generateUniqueTitle } from '../../utils/generate-unique-title';
import { getLocalTemplates } from '../../utils/page-config-lookup';
import {
  buildPathFromEntityType,
  buildPathFromPage,
  buildPathFromRevelioPageId,
  type EntityType,
  filterIdToEntityType,
} from '../../utils/routing-helpers';
import { type Page, PageType } from '../PageConfigProvider/types';
import { useUndoToast } from '../UndoToast';

interface PageManagerContextValue {
  /** Current list of pages available to the user */
  pages: Page[];
  loading: boolean;
  getPage: (id: string) => Page | null;

  // Page mutations
  createOrUpdatePage: (page: Page) => Promise<Page | null>;
  updatePage: (id: string, data: Partial<Page>) => Promise<Page | null>;
  deletePage: (id: string) => void;

  // Navigation actions
  createPageDraft: (
    entityType: EntityType,
    skipGuard?: boolean
  ) => string | null;
  clonePageDraft: (id: string) => Promise<void>;
  clonePage: (id: string) => Promise<void>;
  editPage: (id: string) => void;
  viewPage: (id: string, skipGuard?: boolean) => void;
}

const PageManagerContext = createContext<PageManagerContextValue | undefined>(
  undefined
);

export const PageManagerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isMyPagesEnabled = useUnleashFlag(FeatureFlag.CustomDashViewEditToggle);

  const { data: pagesData = [], isLoading } = usePagesQuery(isMyPagesEnabled);

  const { handleCreatePage, handleUpdatePage, handleDeletePage } =
    usePageMutations();

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const undoToast = useUndoToast();
  const { loggedInUser } = useGetLoggedInUser();

  const localTemplates = getLocalTemplates();

  const pages = isMyPagesEnabled ? pagesData : [];

  const showErrorToast = (message: string, error?: unknown) => {
    if (error) console.error('[PageManagerProvider]', message, error);

    toast({
      title: message,
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const getPage = (id: string): Page | null => {
    return pages.find((p) => p.id === id) ?? null;
  };

  // Create or update a page
  const createOrUpdatePage = async (page: Page): Promise<Page | null> => {
    const isNewPage = !getPage(page.id);

    if (isNewPage) {
      const isLocalTemplateId = localTemplates.some((t) => t.id === page.id);
      const pageToSave = isLocalTemplateId
        ? { ...page, id: crypto.randomUUID() }
        : page;

      try {
        const { _id, ...restPage } = pageToSave as Page & { _id?: string };
        const created = await handleCreatePage.mutateAsync(restPage);
        const pageToUse = created ?? restPage;
        const pageType = pageToUse.type;
        const primaryFilterId =
          pageToUse.tabs?.[0]?.tabContent?.primaryFilter?.id ??
          FilterId.COMPANY;

        const targetPath = buildPathFromEntityType(
          pageType === PageType.REPORT
            ? 'report'
            : filterIdToEntityType(primaryFilterId),
          {
            templateId: pageToUse.id,
            search: location.search,
          }
        );

        // Prevent duplicate redirects
        if (location.pathname !== targetPath) {
          navigate(targetPath, {
            state: { skipGuard: true, preserveScroll: true },
          });
        }

        return pageToUse;
      } catch (error) {
        showErrorToast('Page creation failed', error);
        return null;
      }
    }

    return updatePage(page.id, page);
  };

  const updatePage = async (
    id: string,
    data: Partial<Page> & { _id?: unknown }
  ): Promise<Page | null> => {
    try {
      const { _id, ...page } = data;
      const updated = await handleUpdatePage.mutateAsync({ id, data: page });
      return updated ?? null;
    } catch (error) {
      showErrorToast('Update failed', error);
      return null;
    }
  };

  const deletePage = (id: string): void => {
    const cachedPages =
      queryClient.getQueryData<Page[]>(pageQueryKeys.lists()) ?? [];

    const target = cachedPages.find((p) => p.id === id);
    if (!target) return;

    const redirecting = location.pathname.includes(target.id);

    if (redirecting) {
      navigate('/');
    }

    const UNDO_DURATION = 5000;

    // Immediately trigger delete mutation
    handleDeletePage.mutate(id, {
      onError: (error) => {
        showErrorToast('Delete failed', error);
      },
    });

    undoToast({
      message: 'Page deleted.',
      duration: UNDO_DURATION,
      onUndo: () => {
        handleCreatePage.mutate(target, {
          onSuccess: () => {
            if (redirecting) {
              const primaryFilterId =
                target.tabs?.[0]?.tabContent?.primaryFilter?.id ??
                FilterId.COMPANY;
              const pageType = target.type;
              navigate(
                buildPathFromEntityType(
                  pageType === PageType.REPORT
                    ? 'report'
                    : filterIdToEntityType(primaryFilterId),
                  {
                    templateId: target.id,
                  }
                )
              );
            }
          },
          onError: (error) => {
            showErrorToast('Restore failed', error);
          },
        });
      },
    });
  };

  // Clone an existing page, save it and show a toast. Does not navigate.
  const clonePage = async (id: string): Promise<void> => {
    const page = findPage(id);
    if (!page) return;

    const existingPageNames = getExistingPageNames(
      [...pages, ...localTemplates],
      filterIdToEntityType(
        page.tabs?.[0]?.tabContent?.primaryFilter?.id ?? FilterId.COMPANY
      )
    );
    const newName = generateUniqueTitle(existingPageNames, page.name, 'copy');
    const owner = {
      id: loggedInUser?.id ?? '',
      username: loggedInUser?.username ?? '',
    };

    const cloned = cloneAndSanitizePage(page, owner, newName);

    try {
      await handleCreatePage.mutateAsync(cloned);
      toast({
        title: 'Page copied',
        description: `“${newName}” has been created.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      showErrorToast('Page duplication failed', error);
    }
  };

  const findPage = (id: string): Page | undefined => {
    const localTemplate = localTemplates.find((p) => p.id === id);
    if (localTemplate) {
      return localTemplate;
    }

    const remoteTemplate = pages.find((p) => p.id === id);
    return remoteTemplate;
  };

  const navigateToPage = (
    path: string,
    state?: { skipGuard?: boolean; preserveScroll?: boolean }
  ) => {
    if (location.pathname !== path) {
      navigate(path, { state });
    }
  };

  const createPageDraft = (
    entityType: EntityType,
    skipGuard?: boolean
  ): string | null => {
    try {
      const targetPath = buildPathFromEntityType(entityType, {
        search: location.search,
      });
      if (skipGuard === true) {
        navigate(targetPath, { state: { skipGuard: true } });
      } else {
        navigate(targetPath);
      }
      return targetPath;
    } catch (error) {
      showErrorToast('Page creation failed', error);
      return null;
    }
  };

  const clonePageDraft = async (id: string) => {
    const page = await getOriginalPage({
      id,
      existingPages: pages,
    });
    if (!page) return;

    const clonePath = buildPathFromPage(page, {
      action: 'clone',
      search: location.search,
    });

    navigateToPage(clonePath, { preserveScroll: true });
  };

  const editPage = (id: string) => {
    const page = findPage(id);
    if (!page) return;

    const editPath = buildPathFromPage(page, {
      action: 'edit',
      search: location.search,
    });

    navigateToPage(editPath, { preserveScroll: true });
  };

  const viewPage = (id: string, skipGuard?: boolean) => {
    const page = findPage(id);
    if (!page) return;

    let viewPath = '';

    if (page.revelioPageId) {
      viewPath = buildPathFromRevelioPageId(page.revelioPageId, {
        search: location.search,
      });
    } else {
      viewPath = buildPathFromPage(page, { search: location.search });
    }

    navigateToPage(viewPath, { skipGuard, preserveScroll: true });
  };

  const value: PageManagerContextValue = {
    pages,
    loading: isLoading,
    createPageDraft,
    updatePage,
    deletePage,
    clonePageDraft,
    clonePage,
    editPage,
    viewPage,
    getPage,
    createOrUpdatePage,
  };

  return (
    <PageManagerContext.Provider value={value}>
      {children}
    </PageManagerContext.Provider>
  );
};

export const usePageManager = () => {
  const context = useContext(PageManagerContext);
  if (!context) {
    throw new Error('usePageManager must be used within a PageManagerProvider');
  }
  return context;
};
